import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, Clock, Play, Square, RefreshCw, Users, Building,
  Calendar, ChevronDown, Filter, Download, AlertTriangle,
  CheckCircle, XCircle, Navigation, Target, TrendingUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

export default function PointagePage() {
  const { user, isAssistant } = useOutletContext();
  const [currentStatus, setCurrentStatus] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [nearbyTargets, setNearbyTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pointing, setPointing] = useState(false);
  const [dateRange, setDateRange] = useState('week');
  const [showTargetSelector, setShowTargetSelector] = useState(false);

  // Get GPS position
  const getPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError("La géolocalisation n'est pas supportée par ton navigateur");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentPosition({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
        setGpsError(null);
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGpsError("Autorise l'accès à ta position");
            break;
          case error.POSITION_UNAVAILABLE:
            setGpsError("Position indisponible");
            break;
          case error.TIMEOUT:
            setGpsError("Délai d'attente dépassé");
            break;
          default:
            setGpsError("Erreur de géolocalisation");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  // Fetch current status
  const fetchStatus = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/pointage/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentStatus(data);
        if (data.current_session) {
          setSelectedTarget({
            type: data.current_session.target_type,
            id: data.current_session.target_id,
            name: data.current_session.target_name
          });
        }
      }
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  // Fetch nearby targets
  const fetchNearbyTargets = async () => {
    if (!currentPosition) return;
    
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(
        `${API_URL}/api/pointage/nearby-targets?latitude=${currentPosition.latitude}&longitude=${currentPosition.longitude}&radius=1000`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setNearbyTargets(data);
        // Auto-select closest target if within 200m and no target selected
        if (data.length > 0 && data[0].distance <= 200 && !selectedTarget && !currentStatus?.is_on_site) {
          setSelectedTarget(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching nearby targets:', error);
    }
  };

  // Fetch sessions
  const fetchSessions = async () => {
    const token = localStorage.getItem('client_token');
    const now = new Date();
    let startDate;
    
    switch (dateRange) {
      case 'today':
        startDate = now.toISOString().split('T')[0];
        break;
      case 'week':
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDate = weekAgo.toISOString().split('T')[0];
        break;
      case 'month':
        const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate = monthAgo.toISOString().split('T')[0];
        break;
      default:
        startDate = null;
    }
    
    try {
      let url = `${API_URL}/api/pointage/sessions`;
      if (startDate) url += `?start_date=${startDate}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSessions(await response.json());
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    const token = localStorage.getItem('client_token');
    try {
      const response = await fetch(`${API_URL}/api/pointage/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Create pointage
  const createPointage = async (type) => {
    if (!currentPosition) {
      toast.error("Position GPS requise");
      getPosition();
      return;
    }

    if (!selectedTarget && type === 'arrival') {
      toast.error("Sélectionnez une destination");
      setShowTargetSelector(true);
      return;
    }

    const target = type === 'departure' ? currentStatus?.current_session : selectedTarget;
    if (!target) {
      toast.error("Aucune cible sélectionnée");
      return;
    }

    setPointing(true);
    const token = localStorage.getItem('client_token');

    try {
      const response = await fetch(`${API_URL}/api/pointage`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_type: target.type || target.target_type,
          target_id: target.id || target.target_id,
          pointage_type: type,
          latitude: currentPosition.latitude,
          longitude: currentPosition.longitude,
          accuracy: currentPosition.accuracy
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        if (data.is_valid) {
          toast.success(type === 'arrival' ? 'Arrivée enregistrée !' : 'Départ enregistré !');
        } else {
          toast.warning(data.message);
        }
        fetchStatus();
        fetchSessions();
        fetchStats();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setPointing(false);
    }
  };

  // Initial load
  useEffect(() => {
    setLoading(true);
    getPosition();
    fetchStatus();
    fetchStats();
    setLoading(false);
  }, []);

  // Fetch nearby when position changes
  useEffect(() => {
    if (currentPosition) {
      fetchNearbyTargets();
    }
  }, [currentPosition]);

  // Fetch sessions when date range changes
  useEffect(() => {
    fetchSessions();
  }, [dateRange]);

  // Format duration
  const formatDuration = (minutes) => {
    if (!minutes) return '0min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins > 0 ? mins + 'min' : ''}`;
    }
    return `${mins}min`;
  };

  // Get target icon
  const getTargetIcon = (type) => {
    switch (type) {
      case 'chantier': return <Building className="w-4 h-4" />;
      case 'client': return <Users className="w-4 h-4" />;
      case 'prospect': return <Target className="w-4 h-4" />;
      default: return <MapPin className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="pointage-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="w-6 h-6 text-rm-primary" />
          <div>
            <h1 className="text-xl font-bold text-rm-text">Pointage</h1>
            <p className="text-sm text-slate-500">Géolocalisation et suivi du temps</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => { getPosition(); fetchStatus(); }}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* GPS Status */}
      {gpsError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          <div>
            <p className="font-medium text-red-800">Erreur GPS</p>
            <p className="text-sm text-red-600">{gpsError}</p>
          </div>
          <Button variant="outline" size="sm" onClick={getPosition} className="ml-auto">
            Réessayer
          </Button>
        </div>
      )}

      {currentPosition && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-3">
          <Navigation className="w-5 h-5 text-green-600" />
          <div className="text-sm">
            <span className="text-green-700 font-medium">Position GPS active</span>
            <span className="text-green-600 ml-2">
              (précision: {Math.round(currentPosition.accuracy)}m)
            </span>
          </div>
        </div>
      )}

      {/* Current Status Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        {currentStatus?.is_on_site ? (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Clock className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-rm-text mb-1">
              En cours sur site
            </h2>
            <p className="text-slate-600 mb-2">
              {currentStatus.current_session.target_name}
            </p>
            <p className="text-2xl font-bold text-green-600 mb-4">
              {formatDuration(currentStatus.current_session.duration_minutes)}
            </p>
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => createPointage('departure')}
              disabled={pointing || !currentPosition}
              data-testid="departure-btn"
            >
              {pointing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
              ) : (
                <>
                  <Square className="w-5 h-5 mr-2" />
                  Pointer Départ
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-20 h-20 mx-auto bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <MapPin className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-lg font-semibold text-rm-text mb-4">
              Pas de pointage en cours
            </h2>

            {/* Target Selector */}
            <div className="mb-4">
              <button
                onClick={() => setShowTargetSelector(!showTargetSelector)}
                className="w-full max-w-md mx-auto flex items-center justify-between px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
                data-testid="target-selector"
              >
                <div className="flex items-center gap-2">
                  {selectedTarget ? (
                    <>
                      {getTargetIcon(selectedTarget.type)}
                      <span className="font-medium">{selectedTarget.name}</span>
                      {selectedTarget.distance && (
                        <span className="text-xs text-slate-500">
                          ({selectedTarget.distance}m)
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-500">Sélectionner une destination</span>
                  )}
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showTargetSelector ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showTargetSelector && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mt-2 max-w-md mx-auto bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-60 overflow-y-auto"
                  >
                    {nearbyTargets.length > 0 ? (
                      nearbyTargets.map((target) => (
                        <button
                          key={`${target.type}-${target.id}`}
                          onClick={() => {
                            setSelectedTarget(target);
                            setShowTargetSelector(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 ${
                            selectedTarget?.id === target.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            target.type === 'chantier' ? 'bg-orange-100 text-orange-600' :
                            target.type === 'client' ? 'bg-blue-100 text-blue-600' :
                            'bg-purple-100 text-purple-600'
                          }`}>
                            {getTargetIcon(target.type)}
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium text-sm">{target.name}</p>
                            <p className="text-xs text-slate-500">{target.address}</p>
                          </div>
                          <span className={`text-xs font-medium ${
                            target.distance <= 200 ? 'text-green-600' : 'text-slate-500'
                          }`}>
                            {target.distance}m
                          </span>
                        </button>
                      ))
                    ) : (
                      <div className="p-4 text-center text-slate-500 text-sm">
                        Aucune destination à proximité
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => createPointage('arrival')}
              disabled={pointing || !currentPosition || !selectedTarget}
              data-testid="arrival-btn"
            >
              {pointing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
              ) : (
                <>
                  <Play className="w-5 h-5 mr-2" />
                  Pointer Arrivée
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Clock className="w-4 h-4" />
              <span className="text-xs">Total heures</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">
              {Math.round(stats.total_hours)}h
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Calendar className="w-4 h-4" />
              <span className="text-xs">Sessions</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">
              {stats.total_sessions}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <Building className="w-4 h-4" />
              <span className="text-xs">Sites visités</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">
              {Object.keys(stats.by_target || {}).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-2 text-slate-500 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs">Moy/session</span>
            </div>
            <p className="text-2xl font-bold text-rm-text">
              {stats.total_sessions > 0 
                ? formatDuration(Math.round((stats.total_hours * 60) / stats.total_sessions))
                : '0h'}
            </p>
          </div>
        </div>
      )}

      {/* Sessions History */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-rm-text flex items-center gap-2">
            <Calendar className="w-5 h-5 text-rm-primary" />
            Historique
          </h2>
          <div className="flex items-center gap-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5"
              data-testid="date-range-filter"
            >
              <option value="today">Aujourd'hui</option>
              <option value="week">7 derniers jours</option>
              <option value="month">30 derniers jours</option>
            </select>
          </div>
        </div>

        <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
          {sessions.length > 0 ? (
            sessions.map((session) => (
              <div key={session.id} className="p-4 hover:bg-slate-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      session.target_type === 'chantier' ? 'bg-orange-100 text-orange-600' :
                      session.target_type === 'client' ? 'bg-blue-100 text-blue-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      {getTargetIcon(session.target_type)}
                    </div>
                    <div>
                      <p className="font-medium text-rm-text">{session.target_name}</p>
                      <p className="text-xs text-slate-500">{session.target_address}</p>
                      <p className="text-xs text-slate-400 mt-1">
                        {new Date(session.arrival_time).toLocaleDateString('fr-FR', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short'
                        })}
                        {' • '}
                        {new Date(session.arrival_time).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {' - '}
                        {new Date(session.departure_time).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-rm-primary">
                      {formatDuration(session.duration_minutes)}
                    </p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Aucune session sur cette période</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
